import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Infinity as InfinityIcon,
  Sparkles,
  DollarSign,
  Repeat,
  Share2,
  Users,
  TrendingUp,
  Check,
  ArrowRight,
  Coffee,
  Clock,
  Trophy,
} from 'lucide-react';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';
import { ShareToEarnWidget } from '@/components/refer/ShareToEarnWidget';
import api from '@/services/api';

// Turn 5 → "5th", 1 → "1st", 22 → "22nd" — used in payout-day copy.
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

/**
 * ReferAndEarnPage — public-facing marketing page focused on the ONE
 * message that differentiates Harmonix from every other affiliate
 * program: commissions are **perpetual**. As long as the referred
 * customer keeps recharging / renewing their subscription, the
 * referrer keeps earning. No cap, no expiry.
 *
 * Distinct from ``/reseller`` — that page targets B2B partners who
 * want white-label + multi-tenant reselling. This page targets
 * anyone with an audience (existing customers, creators, consultants,
 * SEOs) who wants to earn recurring income by sharing a link.
 *
 * Route: /refer-and-earn (and redirect /refer to /refer-and-earn).
 */
// ─────────────────────────────────────────────────────────────
// Static content that never depends on live config.
// (Dynamic bits — perks/examples/FAQ — are computed inside the
// component from the /api/affiliate/public-config response.)
// ─────────────────────────────────────────────────────────────
const HOW_IT_WORKS = [
  { step: 1, title: 'Sign up (free)', body: 'Create a free Harmonix account and grab your unique referral link from the dashboard.' },
  { step: 2, title: 'Share it', body: 'Post on socials, add it to your newsletter footer, DM a founder friend. Whatever works for your audience.' },
  { step: 3, title: 'They sign up', body: "When someone clicks your link and starts a paid plan, they're locked in as your referral forever." },
  { step: 4, title: 'You get paid — forever', body: 'Every payment they make lands in your Stripe Connect account on the 5th of each month.' },
];

const AUDIENCE = [
  { icon: Coffee, title: 'Happy customers', body: "You already love using Harmonix. Share the link with peers in your industry and the coffee is on us — forever." },
  { icon: Users, title: 'Newsletter creators', body: 'One footer link. Every subscriber who converts = compounding monthly income you never have to renegotiate.' },
  { icon: TrendingUp, title: 'SEO / affiliate publishers', body: "Long-tail keywords deliver Harmonix signups for years. Unlike hosting affiliates, your commissions never expire." },
];

export default function ReferAndEarnPage() {
  const navigate = useNavigate();
  // Live config from the backend so every commission %, example price
  // and payout figure on this page stays in lock-step with the actual
  // product config in ``routes/affiliate.py::AFFILIATE_TIERS`` and the
  // active plan catalogue. Falls back to safe defaults if the endpoint
  // is momentarily unreachable so the page never renders blank.
  const [config, setConfig] = useState({
    tiers: [
      { slug: 'bronze', name: 'Bronze', min_referrals: 0, commission_rate: 10, color: '#CD7F32' },
      { slug: 'silver', name: 'Silver', min_referrals: 5, commission_rate: 15, color: '#C0C0C0' },
      { slug: 'gold', name: 'Gold', min_referrals: 15, commission_rate: 20, color: '#FFD700' },
      { slug: 'platinum', name: 'Platinum', min_referrals: 50, commission_rate: 25, color: '#E5E4E2' },
    ],
    entry_commission_rate: 10,
    top_commission_rate: 25,
    example_plan: { name: 'Professional', monthly_price: 79, currency: 'USD', entry_monthly_commission: 7.9, top_monthly_commission: 19.75 },
    payout_threshold: 50,
    payout_day_of_month: 5,
    attribution_window_days: 90,
  });

  useEffect(() => {
    let cancelled = false;
    api.get('/affiliate/public-config')
      .then((r) => { if (!cancelled && r?.data) setConfig(r.data); })
      .catch(() => { /* fallback defaults already in state */ });
    return () => { cancelled = true; };
  }, []);

  // Derived helpers used across the marketing sections. Memoised so the
  // page doesn't recompute on every render.
  const { PERKS, EXAMPLES, FAQ, hero } = useMemo(() => {
    const price = config?.example_plan?.monthly_price || 79;
    const priceLabel = `$${price}/mo`;
    const topRate = config?.top_commission_rate || 25;
    const entryRate = config?.entry_commission_rate || 10;
    const topMonthly = config?.example_plan?.top_monthly_commission || (price * topRate / 100);
    const payoutDay = config?.payout_day_of_month || 5;
    const payoutMin = config?.payout_threshold || 50;
    const cookieDays = config?.attribution_window_days || 90;

    // Tier-driven examples so the "casual sharer" hits Silver, "creator"
    // hits Gold and "agency" hits Platinum — this matches what a real
    // affiliate actually earns, unlike the previous flat-30% hardcode.
    const tiersByReferrals = [...(config?.tiers || [])].sort((a, b) => a.min_referrals - b.min_referrals);
    const rateForReferrals = (n) => {
      let rate = tiersByReferrals[0]?.commission_rate || 10;
      for (const t of tiersByReferrals) if (n >= t.min_referrals) rate = t.commission_rate;
      return rate;
    };
    const compute = (n) => {
      const rate = rateForReferrals(n);
      const monthly = n * price * rate / 100;
      return { referrals: n, rate, monthly, yearly: monthly * 12 };
    };

    return {
      hero: { price, priceLabel, topRate, entryRate, topMonthly, payoutDay, payoutMin, cookieDays },
      PERKS: [
        {
          icon: InfinityIcon,
          title: 'Commissions never stop',
          body: 'You earn on every payment your referral makes — month 1, month 24, month 60. There is no lifetime cap, no expiry window, no "12 months only" clause. If they stay, you get paid.',
          accent: 'from-emerald-500 to-teal-500',
        },
        {
          icon: DollarSign,
          title: `Up to ${topRate}% of every dollar`,
          body: `Commissions are tiered — start at ${entryRate}% and scale to ${topRate}% as you drive more referrals. Refer a customer on a ${priceLabel} plan at the top tier and you earn $${topMonthly.toFixed(2)}/mo — for as long as they stay. Their upgrade = your raise.`,
          accent: 'from-amber-500 to-orange-500',
        },
        {
          icon: Repeat,
          title: 'Auto-paid every month',
          body: `Payouts run on the ${ordinal(payoutDay)} of every month via Stripe Connect. Minimum threshold is $${payoutMin}; anything below rolls over. No invoices, no chasing, no support tickets.`,
          accent: 'from-violet-500 to-fuchsia-500',
        },
        {
          icon: Share2,
          title: 'One link. Zero friction.',
          body: `Copy your referral link, share it anywhere — LinkedIn, YouTube, an email footer, a Notion doc. First-click attribution with a ${cookieDays}-day cookie window.`,
          accent: 'from-sky-500 to-cyan-500',
        },
      ],
      EXAMPLES: [
        { ...compute(5),   label: 'Casual sharer' },
        { ...compute(20),  label: 'Newsletter / creator' },
        { ...compute(100), label: 'Consultant / agency', highlighted: true },
      ],
      FAQ: [
        {
          q: 'Wait — what does "perpetual" actually mean?',
          a: "Exactly what it sounds like. Most SaaS affiliate programs pay you for 6 or 12 months and then cut you off. Harmonix pays you for as long as your referral keeps their subscription active — whether that's 3 months or 30 years. No lifetime cap.",
        },
        {
          q: 'How are the commission tiers structured?',
          a: `We reward volume: Bronze pays ${entryRate}% from your first signup, and every 5-15-50 referrals bumps you to Silver → Gold → Platinum (${topRate}% at the top). Your rate applies to ALL your active referrals the moment you promote — not just new ones.`,
        },
        {
          q: 'What happens if my referral upgrades their plan?',
          a: `Your commission goes up too — you always earn your tier % of whatever they're paying that month. A referral upgrading from ${priceLabel} to a bigger plan takes your monthly commission up automatically.`,
        },
        {
          q: 'When do payouts arrive?',
          a: `Every month on the ${ordinal(payoutDay)}, via Stripe Connect. Once your balance clears the $${payoutMin} minimum you're paid automatically. Below $${payoutMin} rolls over to the next month — no earnings are ever forfeited.`,
        },
        {
          q: 'What if my referral cancels and then re-subscribes 8 months later?',
          a: "They're still yours. Once someone is attributed to your referral link, they stay attributed. Even a cancel-and-resubscribe cycle keeps you on the payout.",
        },
        {
          q: 'Can I refer my own business?',
          a: "No — self-referrals are automatically flagged and unpaid. But you can refer clients, colleagues, and friends without limit.",
        },
        {
          q: 'How is this different from the /reseller program?',
          a: "The reseller program is for agencies who want to white-label Harmonix and manage their own client billing. Refer & Earn is for individuals — one click, one commission, no reselling required. Both programs can run in parallel.",
        },
      ],
    };
  }, [config]);

  return (
    <>
      <Helmet>
        <title>Refer & Earn — Perpetual Commissions | Harmonix</title>
        <meta
          name="description"
          content="Refer Harmonix and earn 30% commission on every subscription — forever. No cap, no expiry. As long as your referral keeps paying, you keep earning."
        />
        <link rel="canonical" href="https://harmonix.info/refer-and-earn" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Refer & Earn — Perpetual Commissions",
          "url": "https://harmonix.info/refer-and-earn",
          "description": "Earn 30% recurring commission on every Harmonix subscription you refer — for as long as they stay subscribed. No cap, no expiry.",
        })}</script>
      </Helmet>

      <div className="min-h-screen bg-white" data-testid="refer-and-earn-page">
        <PublicHeader />

        {/* ─── Hero ─── */}
        <section className="relative pt-16 pb-20 px-4 sm:px-6 overflow-hidden">
          {/* Decorative gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-50 via-white to-amber-50/50 pointer-events-none" />
          <div className="absolute top-20 -right-20 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 -left-20 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl pointer-events-none" />

          <div className="relative max-w-5xl mx-auto text-center">
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 mb-6 px-3 py-1">
              <Sparkles className="w-3 h-3 mr-1.5" />
              Perpetual Commission Program
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-[1.1]">
              Refer once.<br />
              <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-amber-600 bg-clip-text text-transparent">
                Earn forever.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-4 leading-relaxed">
              Send someone to Harmonix and earn <strong className="text-slate-900">up to {hero.topRate}% of every dollar they pay us</strong> — for as long as they stay subscribed.
            </p>
            <p className="text-base text-slate-500 max-w-xl mx-auto mb-10">
              No 12-month cliff. No "first year only" clause. No lifetime cap. Just recurring commission that keeps compounding.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button
                size="lg"
                onClick={() => navigate('/login?signup=true&utm_source=refer-and-earn&utm_campaign=perpetual')}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base px-8 h-12 shadow-lg"
                data-testid="refer-start-btn"
              >
                Start referring free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-base px-8 h-12 border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="refer-how-btn"
              >
                See how it works
              </Button>
            </div>
            <p className="text-xs text-slate-400">
              Already have an account?{' '}
              <Link to="/dashboard/refer" className="text-emerald-700 font-semibold underline underline-offset-2">
                Go to your referral dashboard →
              </Link>
            </p>
          </div>
        </section>

        {/* ─── Perpetual explainer strip ─── */}
        <section className="border-y border-emerald-100 bg-emerald-50/60 py-10 px-4 sm:px-6" data-testid="refer-perpetual-strip">
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center gap-6 sm:gap-10 text-center sm:text-left">
            <div className="flex-shrink-0 w-20 h-20 rounded-full bg-white shadow-sm flex items-center justify-center border-4 border-emerald-100">
              <InfinityIcon className="w-9 h-9 text-emerald-600" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900 mb-1">
                The math is simple: your referral pays {hero.priceLabel} for 5 years?
              </p>
              <p className="text-base text-slate-600">
                <strong className="text-emerald-700">
                  ${(hero.topMonthly * 12 * 5).toLocaleString(undefined, { maximumFractionDigits: 0 })} in your pocket
                </strong>{' '}
                from a single 30-second link share (at the top {hero.topRate}% tier). Do that 20 times and you&apos;re looking at{' '}
                <strong className="text-emerald-700">
                  ${(hero.topMonthly * 20 * 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}/year in recurring income
                </strong>.
              </p>
            </div>
          </div>
        </section>

        {/* ─── Tier ladder — driven by /affiliate/public-config so the
             marketing % on the page always matches the real product
             config. If tiers change in the backend, this updates on
             the next page load — no marketing-page re-edit needed. ─── */}
        <section className="py-16 px-4 sm:px-6 bg-white" data-testid="refer-tier-ladder">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="bg-amber-50 text-amber-800 border-amber-200 mb-4">
                <Trophy className="w-3 h-3 mr-1.5" /> Tier ladder
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">Refer more, earn a bigger cut</h2>
              <p className="text-base text-slate-500 max-w-2xl mx-auto">
                Every referral bumps you closer to the next tier — and your new rate applies to <strong>every active referral</strong>, not just future ones.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {config.tiers.map((t, idx) => {
                const isTop = idx === config.tiers.length - 1;
                return (
                  <div
                    key={t.slug}
                    className={`relative rounded-2xl border-2 p-5 transition-all ${
                      isTop ? 'border-emerald-500 bg-emerald-50/50 shadow-md' : 'border-slate-200 bg-white'
                    }`}
                    data-testid={`tier-card-${t.slug}`}
                  >
                    {isTop && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-emerald-600 text-white text-[10px] px-2 py-0.5 shadow-sm">Highest</Badge>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: t.color || '#94a3b8' }}
                      />
                      <p className="text-sm font-bold text-slate-900">{t.name}</p>
                    </div>
                    <p className="text-4xl font-extrabold text-slate-900">
                      {t.commission_rate}<span className="text-lg font-medium text-slate-400">%</span>
                    </p>
                    <p className="text-xs text-slate-500 mt-1 mb-3">
                      {t.min_referrals === 0 ? 'From your first signup' : `${t.min_referrals}+ referrals`}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      At {hero.priceLabel} × {t.commission_rate}% = <strong className="text-emerald-700">${(hero.price * t.commission_rate / 100).toFixed(2)}/mo per referral</strong>
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ─── Why it's different (perks) ─── */}
        <section className="py-20 px-4 sm:px-6" data-testid="refer-perks">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-4">Why it&apos;s different</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">Not another 12-month affiliate scheme</h2>
              <p className="text-base text-slate-500 max-w-2xl mx-auto">
                Most SaaS affiliate programs pay you for a year then cut you off. Ours doesn&apos;t.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              {PERKS.map((p) => (
                <Card key={p.title} className="border-slate-200 hover:shadow-lg transition-all overflow-hidden group">
                  <CardContent className="p-6 sm:p-7">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${p.accent} text-white mb-4 shadow-sm`}>
                      <p.icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{p.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ─── How it works ─── */}
        <section id="how-it-works" className="py-20 px-4 sm:px-6 bg-slate-50/50 border-y border-slate-200" data-testid="refer-how-it-works">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-14">
              <Badge className="bg-amber-50 text-amber-800 border-amber-200 mb-4">How it works</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">4 steps. About 3 minutes.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {HOW_IT_WORKS.map((s) => (
                <div key={s.step} className="relative">
                  <div className="w-12 h-12 rounded-full bg-white border-2 border-emerald-200 text-emerald-700 font-extrabold text-lg flex items-center justify-center mb-4 shadow-sm">
                    {s.step}
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Real earnings calculator strip ─── */}
        <section className="py-20 px-4 sm:px-6" data-testid="refer-earnings">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 mb-4">The math</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3">What you could earn</h2>
              <p className="text-base text-slate-500 max-w-2xl mx-auto">
                Assumes referrals on the {hero.priceLabel} plan. Tier %'s auto-apply once you cross each milestone — {config.tiers.map(t => `${t.min_referrals}+ = ${t.commission_rate}%`).join(', ')}.
              </p>
            </div>

            <div className="grid sm:grid-cols-3 gap-5">
              {EXAMPLES.map((e) => (
                <Card
                  key={e.referrals}
                  className={`${e.highlighted ? 'border-2 border-emerald-500 shadow-xl ring-1 ring-emerald-100 relative' : 'border-slate-200'}`}
                  data-testid={`earnings-card-${e.referrals}`}
                >
                  {e.highlighted && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-emerald-600 text-white text-xs px-3 py-0.5 shadow-sm">
                        <TrendingUp className="w-3 h-3 mr-1" /> Most active
                      </Badge>
                    </div>
                  )}
                  <CardContent className="p-6 text-center">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{e.label}</p>
                    <p className="text-5xl font-extrabold text-slate-900 mb-1">{e.referrals}</p>
                    <p className="text-xs text-slate-500 mb-2">active referrals</p>
                    <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                      {e.rate}% tier
                    </Badge>
                    <div className="pt-4 border-t border-slate-100 space-y-2">
                      <div>
                        <p className="text-2xl font-extrabold text-emerald-700">
                          ${e.monthly.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                          <span className="text-sm font-medium text-slate-400">/mo</span>
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">Recurring monthly income</p>
                      </div>
                      <div>
                        <p className="text-base font-bold text-slate-900">
                          ${e.yearly.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          <span className="text-sm font-medium text-slate-400">/yr</span>
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">Annualised</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-center text-xs text-slate-400 mt-6">
              These aren&apos;t caps. If your referrals stay on 3 more years, so do the payouts. Numbers are illustrative — actual commissions depend on the plan each referral chooses.
            </p>
          </div>
        </section>

        {/* ─── Comparison strip vs typical affiliate programs ─── */}
        <section className="py-16 px-4 sm:px-6 bg-slate-900 text-white" data-testid="refer-compare">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="bg-white/10 text-white border-white/20 mb-4">The difference</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3">Us vs. the rest of the industry</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5">
              <Card className="bg-slate-800 border-slate-700 text-white">
                <CardContent className="p-6">
                  <p className="text-sm font-medium text-slate-400 uppercase tracking-wide mb-4">Typical SaaS affiliate</p>
                  <ul className="space-y-3 text-sm text-slate-300">
                    <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-rose-400 flex-shrink-0" /> Commission caps at 12 months</li>
                    <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-rose-400 flex-shrink-0" /> Payout thresholds of $200+</li>
                    <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-rose-400 flex-shrink-0" /> Attribution resets on cancel/resubscribe</li>
                    <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-rose-400 flex-shrink-0" /> Manual invoicing required</li>
                    <li className="flex items-start gap-2"><Clock className="w-4 h-4 mt-0.5 text-rose-400 flex-shrink-0" /> Commission % often drops after year 1</li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-emerald-600 to-teal-600 border-emerald-500 text-white shadow-xl">
                <CardContent className="p-6">
                  <p className="text-sm font-medium text-emerald-100 uppercase tracking-wide mb-4 flex items-center gap-1">
                    <Sparkles className="w-4 h-4" /> Harmonix Refer &amp; Earn
                  </p>
                  <ul className="space-y-3 text-sm">
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-200 flex-shrink-0" /> Perpetual — no month cap, ever</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-200 flex-shrink-0" /> ${hero.payoutMin} minimum, auto-paid on the {ordinal(hero.payoutDay)}</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-200 flex-shrink-0" /> Sticky attribution — cancel/resub keeps you paid</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-200 flex-shrink-0" /> Stripe Connect — zero paperwork</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 text-emerald-200 flex-shrink-0" /> Tiered {hero.entryRate}% → {hero.topRate}% — for the entire lifetime of the referral</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* ─── Who this is for ─── */}
        <section className="py-20 px-4 sm:px-6" data-testid="refer-audience">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-4">Perfect for</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">Who earns the most</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {AUDIENCE.map((a) => (
                <Card key={a.title} className="border-slate-200 hover:border-emerald-200 hover:shadow-md transition-all">
                  <CardContent className="p-6">
                    <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                      <a.icon className="w-5 h-5" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-2">{a.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed">{a.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FAQ ─── */}
        <section className="py-20 px-4 sm:px-6 bg-slate-50/60 border-y border-slate-200" data-testid="refer-faq">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <Badge className="bg-slate-100 text-slate-700 border-slate-200 mb-4">FAQ</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">The obvious questions</h2>
            </div>
            <div className="space-y-3">
              {FAQ.map((f) => (
                <details key={f.q} className="group bg-white rounded-lg border border-slate-200 p-5 open:border-emerald-200 open:shadow-sm">
                  <summary className="font-semibold text-slate-900 cursor-pointer flex items-center justify-between list-none">
                    <span className="pr-4">{f.q}</span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform flex-shrink-0" />
                  </summary>
                  <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ─── Final CTA ─── */}
        <section className="py-24 px-4 sm:px-6 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 text-white" data-testid="refer-final-cta">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-5xl font-extrabold mb-6 leading-tight">
              Your link. Their subscription. Your monthly cheque, forever.
            </h2>
            <p className="text-lg text-emerald-50 mb-10 max-w-xl mx-auto">
              Sign up in 60 seconds, copy your link, share once, and start earning perpetual commission the day your first referral pays.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/login?signup=true&utm_source=refer-and-earn&utm_campaign=perpetual')}
                className="bg-white hover:bg-slate-50 text-emerald-700 font-bold text-base px-8 h-12 shadow-lg"
                data-testid="refer-final-cta-btn"
              >
                Get my referral link
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/contact?type=demo')}
                className="text-base px-8 h-12 border-white/40 text-white hover:bg-white/10"
                data-testid="refer-final-demo-btn"
              >
                Talk to us first
              </Button>
            </div>
          </div>
        </section>

        <PublicFooter />
        <ShareToEarnWidget />
      </div>
    </>
  );
}
